import React, { useContext, useMemo } from 'react';
import Row from 'components/Common/Row';
import { useLocation } from 'react-router-dom';
import NavSideBarContext from 'contexts/NavSideBarContext';
import useEntity from 'hooks/useEntity';
import { EngagementPortalsContext } from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/PortalsProvider/EngagementPortalsProvider';
import useEntityLink from 'components/GlobalSearch/hooks/useEntityLink';

import MainNavTop from '../MainNavTop';
import MainNavBottom from '../MainNavBottom';
import NavItem from '../NavItem';
import { MainNavDiv, MainNavPrimaryContent } from './PortalsMainNav';

const MappingNavItem = ({
  testId,
  label,
  entity,
  primaryFieldValue,
  index,
}: {
  testId: string;
  label: string;
  entity: EntityDetails.Entity;
  primaryFieldValue: string;
  index: number;
}) => {
  const { pathname } = useLocation();

  const entityLink = useEntityLink({
    entity,
    value: primaryFieldValue,
  });

  const isActive = useMemo(() => {
    if (!entity) {
      return false;
    }

    return (
      pathname.includes(`entities/${entity.primaryField}`) ||
      pathname.includes(`entity/${entity.id}`)
    );
  }, [entity, pathname]);

  const icon = useMemo((): Kingpin.Icon => {
    if (index === 0) {
      return 'navigation-metrics';
    }

    if (index === 1) {
      return 'database';
    }

    return 'navigation-metrics';
  }, [index]);

  if (!entityLink) {
    return null;
  }

  return (
    <NavItem
      testId={testId}
      to={entityLink}
      isActiveOverride={isActive}
      label={label}
      icon={icon}
    />
  );
};

const MappingNavItemGate = ({
  mapping,
  index,
}: {
  mapping: UserManagement.EngagementContentMapping;
  index: number;
}) => {
  const { allEngagementPortals } = useContext(EngagementPortalsContext);
  const { entity } = useEntity({
    entityId: mapping.entityId,
    entityAppId: mapping.entityAppId,
  });

  const portal = useMemo(() => {
    return allEngagementPortals.find((p) =>
      p.entityLinks.some((l) => l.entityId === mapping.entityId),
    );
  }, [allEngagementPortals, mapping.entityId]);

  const entityLink = useMemo(() => {
    if (!entity || !portal) {
      return undefined;
    }

    return portal.entityLinks.find((el) => el.entityId === entity.id);
  }, [entity, portal]);

  if (!entity || !portal || !entityLink) {
    return null;
  }

  return (
    <MappingNavItem
      testId={`${entityLink.navLabel}-ready`}
      label={entityLink.navLabel}
      entity={entity}
      primaryFieldValue={mapping.primaryFieldValue}
      index={index}
    />
  );
};

const MappedViews = ({
  contentSettings,
}: {
  contentSettings: UserManagement.EngagementContentSettings;
}) => {
  return (
    <>
      {contentSettings.mappings.map((mapping, index) => (
        <MappingNavItemGate
          mapping={mapping}
          key={`${mapping.entityId}-${mapping.primaryFieldValue}`}
          index={index}
        />
      ))}
    </>
  );
};

const EngagementModeNav = ({
  contentSettings,
}: {
  contentSettings: UserManagement.EngagementContentSettings;
}) => {
  const { isOpen } = useContext(NavSideBarContext);

  return (
    <Row>
      <MainNavDiv isCollapsed={!isOpen}>
        <MainNavPrimaryContent>
          <MainNavTop />
          <MappedViews contentSettings={contentSettings} />
        </MainNavPrimaryContent>
        <MainNavBottom />
      </MainNavDiv>
    </Row>
  );
};

export default EngagementModeNav;
